/**
 * A one-page PDF of plain text lines, for the demo's document viewer.
 *
 * Built at runtime rather than stored, so each document can carry its own title, and so the xref
 * byte offsets are computed instead of hand-maintained. Helvetica's WinAnsi encoding covers ASCII
 * only here, so non-ASCII (the fixtures' em dashes) is folded before it is written.
 */

export interface SamplePdfLine {
    text: string;
    size: number;
}

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 72;
const LINE_GAP = 1.6;

const toPdfText = (text: string) =>
    text
        .replace(/[–—]/g, '-')
        .replace(/[^\x20-\x7e]/g, '')
        .replace(/[\\()]/g, '\\$&');

export default function buildSamplePdf(lines: SamplePdfLine[]): Uint8Array {
    let y = PAGE_HEIGHT - MARGIN;
    const stream = lines
        .map(({ text, size }) => {
            y -= size * LINE_GAP;
            return `BT /F1 ${size} Tf ${MARGIN} ${y.toFixed(0)} Td (${toPdfText(text)}) Tj ET`;
        })
        .join('\n');

    const objects = [
        '<</Type/Catalog/Pages 2 0 R>>',
        '<</Type/Pages/Kids[3 0 R]/Count 1>>',
        `<</Type/Page/Parent 2 0 R/MediaBox[0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}]/Contents 4 0 R/Resources<</Font<</F1 5 0 R>>>>>>`,
        `<</Length ${stream.length}>>stream\n${stream}\nendstream`,
        '<</Type/Font/Subtype/Type1/BaseFont/Helvetica/Encoding/WinAnsiEncoding>>',
    ];

    let pdf = '%PDF-1.4\n';
    const offsets = objects.map((body, index) => {
        const offset = pdf.length;
        pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
        return offset;
    });
    const xrefOffset = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
    pdf += offsets.map((offset) => `${String(offset).padStart(10, '0')} 00000 n \n`).join('');
    pdf += `trailer\n<</Size ${objects.length + 1}/Root 1 0 R>>\nstartxref\n${xrefOffset}\n%%EOF\n`;

    return Uint8Array.from(pdf, (character) => character.charCodeAt(0));
}
